import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { getFunctions, httpsCallable } from "firebase/functions";

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error, invalid
  const [feedback, setFeedback] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);

  const token = searchParams.get("token");

  const unsubscribeReasons = [
    "No longer interested in the content",
    "Too many emails",
    "Content is not relevant to me",
    "Found better alternatives",
    "Just testing",
    "Other"
  ];

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    handleUnsubscribe();
  }, [token]);

  const handleUnsubscribe = async (reason = null) => {
    try {
      const functions = getFunctions();
      const handleUnsubscribeFunc = httpsCallable(functions, "handleUnsubscribe");

      await handleUnsubscribeFunc({
        token: token,
        reason: reason || selectedReason || null
      });

      setStatus("success");
    } catch (error) {
      console.error("Error unsubscribing:", error);

      if (error.code === "functions/not-found") {
        setStatus("invalid");
      } else {
        setStatus("error");
      }
    }
  };

  const submitFeedback = () => {
    if (selectedReason || feedback) {
      handleUnsubscribe(selectedReason + (feedback ? `: ${feedback}` : ""));
      setShowFeedback(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#3D4B59]">
      <Helmet>
        <title>Unsubscribe - Cosmovex</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-xl flex items-center justify-center shadow-sm transition-shadow">
                <span className="text-lg font-black text-[#221A06]">C</span>
              </div>
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#26313B] group-hover:text-[#A8720E] transition-colors">
                Cosmovex
              </h1>
              <p className="text-xs text-[#8A93A2] font-mono">// Tech Insights</p>
            </div>
          </Link>

          <Link
            to="/blogs"
            className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
          >
            View Blogs
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-6 py-16">
        {status === "loading" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 border-4 border-[#E8B44C] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-[#26313B] mb-2">Processing your request...</h2>
            <p className="text-[#5B6B7C]">Please wait while we unsubscribe you from our newsletter.</p>
          </motion.div>
        )}

        {status === "success" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Success Icon */}
            <div className="w-24 h-24 bg-[#2E7D4F]/10 border-2 border-[#2E7D4F]/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-[#2E7D4F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[#26313B] mb-4">
              Successfully Unsubscribed
            </h1>
            <p className="text-[#5B6B7C] text-lg mb-8">
              You've been removed from our newsletter. We're sorry to see you go!
            </p>

            {!showFeedback && (
              <div className="bg-white border border-[#E1DCCE] rounded-lg p-6 mb-8 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                <p className="text-[#3D4B59] mb-4">
                  Would you mind telling us why you unsubscribed? Your feedback helps us improve.
                </p>
                <button
                  onClick={() => setShowFeedback(true)}
                  className="px-6 py-3 bg-[#F1EEE5] border border-[#E1DCCE] text-[#26313B] rounded-lg hover:bg-[#E9E4D6] transition-all"
                >
                  Share Feedback
                </button>
              </div>
            )}

            {showFeedback && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="bg-white border border-[#E1DCCE] rounded-lg p-6 mb-8 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]"
              >
                <h3 className="text-lg font-semibold text-[#26313B] mb-4">Why are you leaving?</h3>

                <div className="space-y-2 mb-4">
                  {unsubscribeReasons.map((reason) => (
                    <label
                      key={reason}
                      className="flex items-center gap-3 p-3 bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg hover:bg-[#E9E4D6] cursor-pointer transition-all"
                    >
                      <input
                        type="radio"
                        name="reason"
                        value={reason}
                        checked={selectedReason === reason}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        className="w-4 h-4 text-[#A8720E] focus:ring-[#E8B44C]"
                      />
                      <span className="text-[#3D4B59]">{reason}</span>
                    </label>
                  ))}
                </div>

                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Additional comments (optional)..."
                  className="w-full px-4 py-3 bg-white border border-[#E1DCCE] rounded-lg text-[#26313B] placeholder-[#8A93A2] focus:outline-none focus:border-[#D9A32E] focus:ring-2 focus:ring-[#E8B44C]/30 resize-none mb-4"
                  rows={4}
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowFeedback(false)}
                    className="flex-1 px-6 py-3 bg-[#F1EEE5] border border-[#E1DCCE] text-[#3D4B59] rounded-lg hover:bg-[#E9E4D6] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitFeedback}
                    disabled={!selectedReason && !feedback}
                    className={`flex-1 px-6 py-3 bg-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-lg transition-all ${
                      !selectedReason && !feedback
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-[#DEA83B]"
                    }`}
                  >
                    Submit Feedback
                  </button>
                </div>
              </motion.div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/blogs"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-lg hover:bg-[#DEA83B] transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Browse Our Blog
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-[#E1DCCE] text-[#26313B] rounded-lg hover:bg-[#F1EEE5] hover:border-[#D9A32E] transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Go to Homepage
              </Link>
            </div>
          </motion.div>
        )}

        {status === "error" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-24 h-24 bg-[#C24E4E]/10 border-2 border-[#C24E4E]/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-[#C24E4E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[#26313B] mb-4">
              Something Went Wrong
            </h1>
            <p className="text-[#5B6B7C] text-lg mb-8">
              We encountered an error while processing your request. Please try again later.
            </p>

            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-lg hover:bg-[#DEA83B] transition-all"
            >
              Return to Homepage
            </Link>
          </motion.div>
        )}

        {status === "invalid" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-24 h-24 bg-[#E8B44C]/15 border-2 border-[#D9A32E]/60 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-[#A8720E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[#26313B] mb-4">
              Invalid Unsubscribe Link
            </h1>
            <p className="text-[#5B6B7C] text-lg mb-8">
              This unsubscribe link is invalid or has expired. If you're having trouble, please contact us.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-lg hover:bg-[#DEA83B] transition-all"
              >
                Go to Homepage
              </Link>
              <a
                href="mailto:contact@cosmovex.com"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-[#E1DCCE] text-[#26313B] rounded-lg hover:bg-[#F1EEE5] hover:border-[#D9A32E] transition-all"
              >
                Contact Support
              </a>
            </div>
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E1DCCE] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#8A93A2] text-sm">
            &copy; {new Date().getFullYear()} Cosmovex. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy-policy" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Terms of Service
            </Link>
            <Link to="/about" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              About
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UnsubscribePage;
